import type { GanttFeature } from '@/components/kibo-ui/gantt'
import SalaryGantt from '@/components/kibo-ui/gantt/salary-gantt'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useActivityTypeLabels } from '@/modules/activities/use-activity-type-labels'
import { GRAPH_WORK_PERIOD } from '@/modules/salary/queries'
import { format } from '@/utils'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { parseISO } from 'date-fns'
import { useCallback, useMemo, useState } from 'react'
import { LazyRouteMap } from './lazy-route-map'
import type { RoutePin, RoutePoint, VehicleRoute } from './types'
import type { UsedVehicle } from './used-vehicles'
import { useVerticalSplit } from './use-vertical-split'

const FERRY_PIN_COLOR = '#1D4ED8'

interface WorkPeriodMapDialogProps {
  workPeriodId: string
  routes: VehicleRoute[]
  usedVehicles: UsedVehicle[]
  activitiesOnly?: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** The route sample closest in space to a point, used to place ferry markers. */
const nearestSample = (routes: VehicleRoute[], at: Date) => {
  const target = at.getTime()
  let closest: { lat: number; lng: number; distance: number } | null = null

  for (const route of routes)
    for (const sample of route.samples) {
      const distance = Math.abs(new Date(sample.timestamp).getTime() - target)
      if (closest && closest.distance <= distance) continue
      closest = { lat: sample.lat, lng: sample.lng, distance }
    }

  return closest
}

export const WorkPeriodMapDialog: React.FC<WorkPeriodMapDialogProps> = ({
  workPeriodId,
  routes,
  usedVehicles,
  activitiesOnly,
  open,
  onOpenChange,
}) => {
  const translations = useTranslate({
    map: 'common.map',
    vehicles: 'common.vehicles',
    activity: 'common.activity',
    noData: 'common.no-data',
    ferryStart: 'gantt.ferry-start',
    ferryEnd: 'gantt.ferry-end',
    position: 'salary.map-position',
    detailHint: 'salary.map-detail-hint',
  })
  const activityTypeLabels = useActivityTypeLabels()

  const [hoveredFeature, setHoveredFeature] = useState<GanttFeature | null>(
    null
  )
  // A clicked activity keeps its positions on the map after the cursor leaves
  // the chart; hovering another one previews it without losing the pin.
  const [pinnedFeature, setPinnedFeature] = useState<GanttFeature | null>(null)
  const [selectedPoint, setSelectedPoint] = useState<RoutePoint | null>(null)
  const { mapHeight, containerRef, startResize } = useVerticalSplit({
    initialRatio: 0.58,
    minTopPx: 200,
    minBottomPx: 200,
  })

  const { data } = useQuery(GRAPH_WORK_PERIOD, {
    variables: { id: workPeriodId },
    skip: !open,
  })
  const workPeriod = data?.workPeriod
  const ferryConditions = workPeriod?.ferryConditions

  const activityAt = useCallback(
    (at: Date) => {
      const time = at.getTime()
      const activity = workPeriod?.activities?.find(
        candidate =>
          parseISO(candidate.datetimeStart).getTime() <= time &&
          parseISO(candidate.datetimeEnd).getTime() >= time
      )

      if (!activity) return null
      return activityTypeLabels[activity.activityType] ?? activity.activityType
    },
    [activityTypeLabels, workPeriod?.activities]
  )

  const ferryPins = useMemo<RoutePin[]>(() => {
    if (!ferryConditions) return []

    return ferryConditions.flatMap(condition => {
      const at = parseISO(condition.timestamp)
      const near = nearestSample(routes, at)
      if (!near) return []

      return [
        {
          id: condition.id,
          lat: near.lat,
          lng: near.lng,
          color: FERRY_PIN_COLOR,
          label: `${
            condition.type === 'FERRY_START'
              ? translations.ferryStart
              : translations.ferryEnd
          } ${format(at, 'HH:mm')}`,
        },
      ]
    })
  }, [ferryConditions, routes, translations.ferryEnd, translations.ferryStart])

  const activeFeature = hoveredFeature ?? pinnedFeature

  const highlight = useMemo(
    () =>
      activeFeature
        ? { start: activeFeature.startAt, end: activeFeature.endAt }
        : null,
    [activeFeature]
  )

  const togglePinnedFeature = useCallback((feature: GanttFeature) => {
    setPinnedFeature(current => (current?.id === feature.id ? null : feature))
  }, [])

  const extraMarkers = useMemo(() => {
    if (!selectedPoint) return []

    return [
      {
        id: 'map-position',
        date: parseISO(selectedPoint.sample.timestamp),
        label: translations.position,
        color: selectedPoint.color,
      },
    ]
  }, [selectedPoint, translations.position])

  const title = workPeriod?.user?.fullName ?? translations.map
  const subtitle = workPeriod?.datetimeStart
    ? format(parseISO(workPeriod.datetimeStart), 'EEEE d. MMMM yyyy')
    : ''

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[96vh] w-[98vw] max-w-none flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:max-w-none">
        <DialogHeader className="flex-none border-b border-stone-200 px-5 py-4">
          <DialogTitle className="text-base font-semibold text-stone-900">
            {title}
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            {subtitle ? `${subtitle} · ` : ''}
            {translations.detailHint}
          </DialogDescription>
          {usedVehicles.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 pt-2 text-xs text-stone-600">
              <span className="font-semibold uppercase tracking-[0.12em] text-stone-500">
                {translations.vehicles}
              </span>
              {usedVehicles.map(vehicle => (
                <span
                  key={vehicle.label}
                  className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-3 py-1 font-semibold text-stone-800"
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: vehicle.color ?? '#A8A29E' }}
                  />
                  {vehicle.label}
                </span>
              ))}
            </div>
          )}
        </DialogHeader>

        <div ref={containerRef} className="flex min-h-0 flex-1 flex-col">
          {routes.length > 0 ? (
            <LazyRouteMap
              className="w-full flex-none"
              style={{ height: mapHeight ?? '58%' }}
              routes={routes}
              highlight={highlight}
              selected={selectedPoint}
              onSelect={point => {
                setSelectedPoint(point)
                // Clicking bare map is how the pinned activity is cleared.
                if (!point) setPinnedFeature(null)
              }}
              pins={ferryPins}
              renderPointDetails={point => {
                const at = parseISO(point.sample.timestamp)
                const activity = activityAt(at)

                return (
                  <div className="space-y-0.5 text-xs">
                    <div className="font-semibold text-stone-900">
                      {format(at, 'HH:mm')} · {point.vehicleLabel}
                    </div>
                    <div className="text-stone-600">
                      {translations.activity}: {activity ?? translations.noData}
                    </div>
                  </div>
                )
              }}
            />
          ) : (
            <div
              className="flex flex-none items-center justify-center text-sm text-stone-500"
              style={{ height: mapHeight ?? '58%' }}
            >
              {translations.noData}
            </div>
          )}

          <div
            onPointerDown={startResize}
            className="flex h-3 flex-none cursor-ns-resize touch-none items-center justify-center border-y border-stone-200 bg-stone-50"
            aria-hidden
          >
            <span className="h-0.5 w-10 rounded-full bg-stone-300" />
          </div>

          <div className="min-h-0 flex-1 overflow-auto px-4 py-3">
            <SalaryGantt
              workPeriodId={workPeriodId}
              activitiesOnly={activitiesOnly}
              fullWidth
              onFeatureHover={setHoveredFeature}
              onFeatureSelect={togglePinnedFeature}
              extraMarkers={extraMarkers}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
